import Box from "@mui/material/Box";
import { useTheme } from "@mui/material";

import isString from "lodash/isString";
import { useEffect, useMemo, useRef, useState } from "react";
import Placeholder from "@tiptap/extension-placeholder";
// ─── TipTap Imports ───────────────────────────────────────────────────────────
// npm install @tiptap/react @tiptap/starter-kit @tiptap/extension-color
//             @tiptap/extension-text-style @tiptap/extension-underline
//             @tiptap/extension-link @tiptap/extension-mention
//             @tiptap/extension-font-size @tiptap/extension-text-align
//             @tiptap/pm
// If @tiptap/extension-font-size is unavailable, see the inline extension below.

import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-text-style";
import Mention from "@tiptap/extension-mention";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import { Node, mergeAttributes } from "@tiptap/core";
import { useSelector } from "react-redux";

// ─── Minimal Image node ───────────────────────────────────────────────────────
// Self-contained block image node (avoids pulling in @tiptap/extension-image).
// Renders/parses a plain <img src>; base64 data URLs are supported, which is
// how pasted screenshots and migrated BlockNote images are stored.
const Image = Node.create({
  name: "image",
  group: "block",
  inline: false,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: "img[src]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes)];
  },
  addCommands() {
    return {
      setImage:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

// ─── BlockNote → TipTap HTML normalizer ───────────────────────────────────────
// Existing Underwriting Notes were authored in BlockNote, which persists an
// `htmlString` using its own checklist/image markup. Convert that markup into
// the shapes TipTap's TaskList/TaskItem + Image extensions parse, so legacy
// notes load without data loss. Idempotent: HTML already produced by TipTap has
// no BlockNote markers, so it passes through unchanged.
export const normalizeBlockNoteHtml = (html) => {
  if (typeof html !== "string" || !html.trim()) return html;
  // Cheap bail-out when there's nothing BlockNote-specific to convert.
  if (!/data-checked|<figure|data-url|bn-inline-content/i.test(html)) {
    return html;
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      `<div id="__bnroot">${html}</div>`,
      "text/html",
    );
    const root = doc.getElementById("__bnroot");

    // Checklists: BlockNote emits <ul><li data-checked><input type=checkbox>…
    // → TipTap wants <ul data-type="taskList"><li data-type="taskItem" …>.
    root.querySelectorAll("ul, ol").forEach((list) => {
      const checkItems = list.querySelectorAll(":scope > li[data-checked]");
      if (!checkItems.length) return;
      list.setAttribute("data-type", "taskList");
      checkItems.forEach((li) => {
        const checked = li.getAttribute("data-checked") === "true";
        li.setAttribute("data-type", "taskItem");
        li.setAttribute("data-checked", checked ? "true" : "false");
        const input = li.querySelector('input[type="checkbox"]');
        if (input) input.remove();
      });
    });

    // Images: BlockNote wraps them as <figure data-url><img></figure>.
    root.querySelectorAll("figure").forEach((fig) => {
      const dataUrl = fig.getAttribute("data-url");
      let img = fig.querySelector("img");
      if (!img && dataUrl) img = doc.createElement("img");
      if (img) {
        if (!img.getAttribute("src") && dataUrl) img.setAttribute("src", dataUrl);
        fig.replaceWith(img);
      }
    });

    // Strip BlockNote helper classes so they don't leak into stored HTML.
    root
      .querySelectorAll(".bn-inline-content")
      .forEach((el) => el.removeAttribute("class"));

    return root.innerHTML;
  } catch {
    return html;
  }
};

// ─── Inline FontSize extension (avoids extra package) ─────────────────────────
// If you have @tiptap/extension-font-size installed, swap this out for:
//   import FontSize from "@tiptap/extension-font-size";

const FontSize = TextStyle.extend({
  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
});

// ─── Toolbar Button ───────────────────────────────────────────────────────────

const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      border: "none",
      borderRadius: 4,
      cursor: disabled ? "default" : "pointer",
      background: isActive ? "#e0e7ff" : "transparent",
      color: isActive ? "#3b5998" : disabled ? "#ccc" : "#555",
      fontSize: 13,
      fontWeight: isActive ? 700 : 500,
      transition: "background 0.15s, color 0.15s",
    }}
    onMouseEnter={(e) => {
      if (!disabled)
        e.currentTarget.style.background = isActive ? "#e0e7ff" : "#f1f5f9";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = isActive ? "#e0e7ff" : "transparent";
    }}
  >
    {children}
  </button>
);

// ─── Font Size Dropdown ───────────────────────────────────────────────────────

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96];

const FontSizeDropdown = ({ editor }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Detect current font size
  const currentSize =
    editor?.getAttributes("textStyle")?.fontSize?.replace("px", "") || "12";

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const applySize = (size) => {
    editor
      .chain()
      .focus()
      .setMark("textStyle", { fontSize: `${size}px` })
      .run();
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          height: 28,
          padding: "0 8px",
          border: "1px solid #e2e8f0",
          borderRadius: 4,
          background: "#fff",
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 600,
          color: "#475569",
          minWidth: 44,
        }}
      >
        {currentSize}
        <span style={{ fontSize: 10, marginLeft: 2 }}>▾</span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 1000,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
            maxHeight: 200,
            overflowY: "auto",
            marginTop: 2,
            minWidth: 50,
          }}
        >
          {FONT_SIZES.map((size) => (
            <div
              key={size}
              onClick={() => applySize(size)}
              style={{
                padding: "4px 12px",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: String(size) === currentSize ? 700 : 400,
                background:
                  String(size) === currentSize ? "#f1f5f9" : "transparent",
                color: "#334155",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f1f5f9")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  String(size) === currentSize ? "#f1f5f9" : "transparent")
              }
            >
              {size}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Color Picker Button ──────────────────────────────────────────────────────

const ColorPickerButton = ({ editor }) => {
  const inputRef = useRef(null);
  const currentColor = editor?.getAttributes("textStyle")?.color || "#000000";

  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <ToolbarButton
        title="Text Color"
        onClick={() => inputRef.current?.click()}
      >
        <span
          style={{ borderBottom: `2px solid ${currentColor}`, lineHeight: 1 }}
        >
          A
        </span>
      </ToolbarButton>
      <input
        ref={inputRef}
        type="color"
        value={currentColor}
        onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        style={{
          position: "absolute",
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

// ─── Image insert button ──────────────────────────────────────────────────────

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ImageButton = ({ editor }) => {
  const inputRef = useRef(null);

  const handleFiles = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    try {
      const src = await fileToDataUrl(file);
      editor.chain().focus().setImage({ src, alt: file.name }).run();
    } catch (err) {
      console.error("HtmlEditor: failed to read image", err);
    }
  };

  return (
    <>
      <ToolbarButton title="Insert Image" onClick={() => inputRef.current?.click()}>
        🖼
      </ToolbarButton>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFiles}
        style={{ display: "none" }}
      />
    </>
  );
};

// ─── Toolbar ──────────────────────────────────────────────────────────────────

const EditorToolbar = ({ editor, enableTaskList, enableImages }) => {
  if (!editor) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "6px 8px",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        background: "#fafbfc",
        flexWrap: "wrap",
      }}
    >
      <FontSizeDropdown editor={editor} />

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      <ColorPickerButton editor={editor} />

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      <ToolbarButton
        title="Bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
      >
        <strong>B</strong>
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
      >
        <em>I</em>
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive("underline")}
      >
        <span style={{ textDecoration: "underline" }}>U</span>
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive("strike")}
      >
        <span style={{ textDecoration: "line-through" }}>S</span>
      </ToolbarButton>

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      <ToolbarButton
        title="Bullet List"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
      >
        •≡
      </ToolbarButton>
      <ToolbarButton
        title="Ordered List"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
      >
        1.
      </ToolbarButton>
      {enableTaskList && (
        <ToolbarButton
          title="Checklist"
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          isActive={editor.isActive("taskList")}
        >
          ☑
        </ToolbarButton>
      )}

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      {enableImages && <ImageButton editor={editor} />}

      <ToolbarButton
        title="Link"
        onClick={() => {
          const url = window.prompt("Enter URL:");
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        isActive={editor.isActive("link")}
      >
        🔗
      </ToolbarButton>
      <ToolbarButton
        title="Unlink"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")}
      >
        ⊘
      </ToolbarButton>

      <div
        style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }}
      />

      <ToolbarButton
        title="Undo"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        ↩
      </ToolbarButton>
      <ToolbarButton
        title="Redo"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        ↪
      </ToolbarButton>
      <ToolbarButton
        title="Clear Formatting"
        onClick={() =>
          editor.chain().focus().clearNodes().unsetAllMarks().run()
        }
      >
        ✕
      </ToolbarButton>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const HtmlEditor = ({
  sx,
  onChange,
  editorRootSx,
  editorToolbarSx,
  editorMainSx,
  suppressOuterBorder,
  maxHeight = "240px",
  readOnly = false,
  id,
  minHeight,
  placeholder = "Start typing...",
  borderRadius = "8px",
  backgroundColor,
  children,
  /**
   * Accepts an HTML string to pre-populate the editor.
   */
  value,
  // ── Opt-in extras (default off → no impact on existing consumers) ──
  enableTaskList = false, // checklist (TaskList/TaskItem) support
  enableImages = false, // inline images + image paste
  convertBlockNoteHtml = false, // normalize legacy BlockNote `value` on load
  alwaysShowToolbar = false, // keep toolbar visible even when empty
}) => {
  const theme = useTheme();
  const users = useSelector((state) => state?.users);
  const lastExternalValue = useRef(null);

  // Build mention suggestion items from the `users` Redux slice (the same list
  // that backs SelectUserEmailInput / "Permitted Users On Loan").
  const mentionSuggestionItems = useMemo(
    () =>
      (users || [])
        .filter((u) => u?.emailAddress)
        .map((user) => ({
          id: user?.emailAddress,
          label: user?.fullName
            ? `${user.fullName} <${user.emailAddress}>`
            : user.emailAddress,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users],
  );

  // The TipTap editor below captures `mentionSuggestion` ONCE at init, so a
  // suggestion that closed over `mentionSuggestionItems` directly would freeze
  // an empty list whenever users load into Redux after this editor mounts.
  // Read the items from a ref instead so the dropdown always reflects the
  // latest users regardless of fetch timing.
  const mentionItemsRef = useRef([]);
  useEffect(() => {
    mentionItemsRef.current = mentionSuggestionItems;
  }, [mentionSuggestionItems]);

  // Mention suggestion renderer (stable; reads the live items via the ref).
  const mentionSuggestion = useMemo(
    () => ({
      items: ({ query }) => {
        const all = mentionItemsRef.current || [];
        const q = (query || "").toLowerCase();
        // No query → show the full user list; typing filters it down.
        if (!q) return all;
        return all.filter((item) => item.label.toLowerCase().includes(q));
      },
      render: () => {
        let popup;
        let latestProps = null;
        let selectedIndex = 0;

        return {
          onStart: (props) => {
            const el = document.createElement("div");
            // Must sit above the drilldown sidebar pane (zIndex 13000) and the
            // date picker popper inside it (13500), or the dropdown renders
            // behind the sidebar and appears to "not show up".
            el.style.cssText = `
              position: fixed;
              z-index: 14000;
              background: #fff;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              box-shadow: 0 4px 16px rgba(0,0,0,0.12);
              max-height: 200px;
              overflow-y: auto;
              padding: 4px 0;
            `;
            document.body.appendChild(el);
            popup = el;
            updatePopup(props);
          },
          // onUpdate: ({ editor: ed }) => {
          //   if (!onChange) return;
          //   let htmlString = ed?.getHTML();
          //   // Strip trailing empty paragraph tags
          //   htmlString = htmlString.replace(/(<p><\/p>)+$/i, "");
          //   const plainText = ed?.getText();
          //   onChange({
          //     htmlString,
          //     plainText,
          //     editorInstance: ed,
          //   });
          // },
          onUpdate: (props) => {
            updatePopup(props);
          },
          // Keyboard selection: ↑/↓ move the highlight, Enter/Tab pick it,
          // Escape closes. Returning true stops the editor from also handling
          // the key (otherwise ↓ moves the caret and Tab leaves the field).
          onKeyDown: ({ event }) => {
            if (event.key === "Escape") {
              popup?.remove();
              popup = null;
              return true;
            }
            const items = latestProps?.items || [];
            if (!popup || items.length === 0) return false;
            if (event.key === "ArrowDown") {
              selectedIndex = (selectedIndex + 1) % items.length;
              renderItems();
              return true;
            }
            if (event.key === "ArrowUp") {
              selectedIndex = (selectedIndex - 1 + items.length) % items.length;
              renderItems();
              return true;
            }
            if (event.key === "Enter" || event.key === "Tab") {
              const item = items[selectedIndex];
              if (item) latestProps.command(item);
              return true;
            }
            return false;
          },
          onExit: () => {
            popup?.remove();
            popup = null;
          },
        };

        function updatePopup(props) {
          if (!popup) return;
          // A new query means a new list — start the highlight at the top.
          if (latestProps && latestProps.query !== props.query) {
            selectedIndex = 0;
          }
          latestProps = props;
          const { clientRect } = props;
          if (!clientRect) return;

          const rect = clientRect();
          popup.style.top = `${rect.bottom + 4}px`;
          popup.style.left = `${rect.left}px`;

          renderItems();
        }

        function renderItems() {
          if (!popup || !latestProps) return;
          const { items, command } = latestProps;
          if (selectedIndex >= items.length) selectedIndex = 0;

          popup.innerHTML = "";
          items.forEach((item, index) => {
            const div = document.createElement("div");
            div.textContent = item.label;
            const isSelected = index === selectedIndex;
            div.style.cssText = `
              padding: 6px 12px;
              cursor: pointer;
              font-size: 12px;
              color: #334155;
              background: ${isSelected ? "#f1f5f9" : "transparent"};
            `;
            div.addEventListener("mouseenter", () => {
              // Hover and keyboard share one highlight.
              if (selectedIndex === index) return;
              selectedIndex = index;
              renderItems();
            });
            div.addEventListener("mousedown", (e) => {
              e.preventDefault();
              command(item);
            });
            popup.appendChild(div);
            if (isSelected) div.scrollIntoView({ block: "nearest" });
          });

          if (items.length === 0) {
            popup.innerHTML =
              '<div style="padding:8px 12px;font-size:12px;color:#94a3b8;">No results</div>';
          }
        }
      },
    }),
    // Stable: items are read live from mentionItemsRef, so this never needs to
    // be rebuilt when the user list changes.
    [],
  );

  // TipTap editor instance
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        // Disable extensions that StarterKit v3 bundles so we can register
        // our own configured copies below without "Duplicate extension names"
        // warnings.
        link: false,
        textStyle: false,
        underline: false,
      }),
      Underline,
      TextStyle,
      FontSize,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: true,
        showOnlyCurrent: true,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: "wysiwyg-mention",
        },
        suggestion: mentionSuggestion,
        renderHTML: ({ node }) => {
          return [
            "a",
            {
              class: "wysiwyg-mention",
              "data-mention": "",
              "data-value": node.attrs.id,
              href: "#",
            },
            `@${node.attrs.label || node.attrs.id}`,
          ];
        },
      }),
      ...(enableTaskList ? [TaskList, TaskItem.configure({ nested: true })] : []),
      ...(enableImages ? [Image] : []),
    ],
    content: "",
    editable: !readOnly,
    editorProps: {
      attributes: {
        // class: "tiptap-editor-content",
        class: "tiptap-prosemirror",
        style: `font-family: var(--primaryFont); font-size: 12px;`,
      },
      // Paste screenshots / images as inline base64 (e.g. copied from Excel).
      ...(enableImages && {
        handlePaste: (view, event) => {
          const items = event.clipboardData?.items;
          if (!items) return false;
          for (const item of items) {
            if (item.type.startsWith("image/")) {
              event.preventDefault();
              const file = item.getAsFile();
              if (file) {
                const reader = new FileReader();
                reader.onload = () => {
                  const imgNode = view.state.schema.nodes.image?.create({
                    src: reader.result,
                  });
                  if (imgNode) {
                    view.dispatch(view.state.tr.replaceSelectionWith(imgNode));
                  }
                };
                reader.readAsDataURL(file);
              }
              return true;
            }
          }
          return false;
        },
      }),
    },
    onUpdate: ({ editor: ed }) => {
      if (!onChange) return;
      const htmlString = ed.getHTML();
      const plainText = ed.getText();
      onChange({
        htmlString,
        plainText,
        editorInstance: ed,
      });
    },
  });

  // Sync external `value` prop into editor
  // Sync external `value` prop into editor
  // useEffect(() => {
  //   if (!editor) return;

  //   // Strip trailing empty paragraph tags
  //   const cleanValue = isString(value)
  //     ? value.replace(/(<p><\/p>)+$/i, "")
  //     : value;

  //   if (isString(cleanValue) && cleanValue.trim() !== "") {
  //     if (lastExternalValue.current !== cleanValue) {
  //       lastExternalValue.current = cleanValue;
  //       editor.commands.setContent(cleanValue, false);
  //     }
  //   } else if (
  //     !cleanValue ||
  //     (isString(cleanValue) && cleanValue.trim() === "")
  //   ) {
  //     if (lastExternalValue.current !== "") {
  //       lastExternalValue.current = "";
  //       editor.commands.clearContent();
  //     }
  //   }
  // }, [value, editor]);
  useEffect(() => {
    if (!editor) return;

    // TipTap v3 changed `setContent`'s signature: the second arg is now an
    // options object (`{ emitUpdate, parseOptions, ... }`), not a boolean.
    // Passing `false` silently falls back to the default `emitUpdate: true`,
    // which fires `onUpdate` on mount with TipTap's normalized HTML and
    // makes RFF flag the field as dirty before the user types anything.
    if (isString(value) && value.trim() !== "") {
      // Only set content if the value actually changed from outside
      // (prevents cursor-resetting loops during typing). Compare on the raw
      // `value` (pre-normalization) so a stable prop stays stable.
      if (lastExternalValue.current !== value) {
        lastExternalValue.current = value;
        const incoming = convertBlockNoteHtml
          ? normalizeBlockNoteHtml(value)
          : value;
        editor.commands.setContent(incoming, { emitUpdate: false });
      }
    } else if (!value || (isString(value) && value.trim() === "")) {
      if (lastExternalValue.current !== "") {
        lastExternalValue.current = "";
        editor.commands.clearContent({ emitUpdate: false });
      }
    }
  }, [value, editor, convertBlockNoteHtml]);

  // Update editable state if readOnly prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  // Check if content is empty (for conditional toolbar display)
  const isEditorEmpty = !editor?.getText()?.trim();

  // ─── Styles ───────────────────────────────────────────────────────────────

  const rootStyles = useMemo(() => {
    if (readOnly) {
      return {
        "& .tiptap-editor-content": {
          "div, span, p, li": {
            fontFamily: "var(--primaryFont)",
            fontSize: "12px",
          },
          "& p": {
            margin: "0 0 0.4em 0",
            fontFamily: "var(--primaryFont)",
            fontSize: "12px",
            lineHeight: 1.5,
          },
          "& ul, & ol": {
            paddingLeft: "1.5em",
            margin: "0.25em 0",
            fontFamily: "var(--primaryFont)",
            fontSize: "12px",
          },
          "& ol": {
            listStyleType: "decimal",
          },
          "& ul": {
            listStyleType: "disc",
          },
          "& li": {
            fontFamily: "var(--primaryFont)",
            fontSize: "12px",
            lineHeight: 1.5,
          },
          "& a": {
            color: theme.palette.primary?.main || "#1976d2",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          },
          "& .wysiwyg-mention": {
            color: theme.palette.primary?.main || "#1976d2",
            fontWeight: 600,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          },
          // Match the editable view's body padding so read-only revisions
          // aren't flush against the pane edge.
          "& .tiptap-prosemirror": {
            px: 1.5,
            py: 1,
          },
          // Checklist (TaskList/TaskItem): without these the task-list <ul>
          // falls through to `listStyleType: disc` above and renders a bullet
          // beside each checkbox. Mirror the editable layout (checkbox + text).
          "& ul[data-type='taskList']": {
            listStyle: "none",
            paddingLeft: 0,
          },
          "& ul[data-type='taskList'] li": {
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
          },
          "& ul[data-type='taskList'] li > label": {
            marginTop: "2px",
            userSelect: "none",
          },
          "& ul[data-type='taskList'] li > div": {
            flex: "1 1 auto",
          },
          "& ul[data-type='taskList'] li > div > p": {
            margin: 0,
          },
          ...editorMainSx,
        },
        ...sx,
      };
    }

    return {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      borderRadius,
      backgroundColor: "#fff",
      border: suppressOuterBorder
        ? "none"
        : `1px solid ${theme.palette.separator || "rgba(0,0,0,0.12)"}`,
      overflow: "hidden",
      ...editorRootSx,

      // Editor content area
      "& .tiptap-editor-content": {
        backgroundColor: backgroundColor || "#fff",
        minHeight: isEditorEmpty ? minHeight : "180px",
        maxHeight,
        overflowY: "auto",
        borderBottomLeftRadius: borderRadius,
        borderBottomRightRadius: borderRadius,
        ...editorMainSx,
      },

      "& .tiptap-prosemirror": {
        outline: "none",
        px: 1.5,
        py: 1,

        "& p": {
          margin: "0 0 0.4em 0",
          fontFamily: "var(--primaryFont)",
          fontSize: "12px",
          lineHeight: 1.5,
        },
        "& ul, & ol": {
          paddingLeft: "1.5em",
          margin: "0.25em 0",
          fontFamily: "var(--primaryFont)",
          fontSize: "12px",
        },
        "& li": {
          fontFamily: "var(--primaryFont)",
          fontSize: "12px",
          lineHeight: 1.5,
        },
        // Checklist (TaskList/TaskItem)
        "& ul[data-type='taskList']": {
          listStyle: "none",
          paddingLeft: 0,
        },
        "& ul[data-type='taskList'] li": {
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
        },
        "& ul[data-type='taskList'] li > label": {
          marginTop: "2px",
          userSelect: "none",
        },
        "& ul[data-type='taskList'] li > div": {
          flex: "1 1 auto",
        },
        "& ul[data-type='taskList'] li > div > p": {
          margin: 0,
        },
        "& img": {
          maxWidth: "100%",
          height: "auto",
          borderRadius: "4px",
          margin: "6px 0",
        },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          fontFamily: "var(--primaryFont)",
          marginTop: "0.6em",
          marginBottom: "0.3em",
        },
        "& h6": {
          fontSize: "12px",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 700,
        },
        "& a": {
          color: theme.palette.primary?.main || "#1976d2",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        },
        "& .wysiwyg-mention": {
          color: theme.palette.primary?.main || "#1976d2",
          fontWeight: 600,
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        },
        "& p.is-editor-empty:first-of-type::before": {
          content: "attr(data-placeholder)",
          float: "left",
          color: "#adb5bd",
          pointerEvents: "none",
          height: 0,
          fontStyle: "italic",
          fontSize: "12px",
        },
      },
      // "& .tiptap-editor-content": {
      //   backgroundColor: backgroundColor || "#fff",
      //   minHeight: isEditorEmpty ? minHeight : "180px",
      //   maxHeight,
      //   overflowY: "auto",
      //   px: 1.5,
      //   py: 1,
      //   outline: "none",
      //   borderBottomLeftRadius: borderRadius,
      //   borderBottomRightRadius: borderRadius,

      //   "& p": {
      //     margin: "0 0 0.4em 0",
      //     fontFamily: "var(--primaryFont)",
      //     fontSize: "12px",
      //     lineHeight: 1.5,
      //   },
      //   "& ul, & ol": {
      //     paddingLeft: "1.5em",
      //     margin: "0.25em 0",
      //     fontFamily: "var(--primaryFont)",
      //     fontSize: "12px",
      //   },
      //   "& li": {
      //     fontFamily: "var(--primaryFont)",
      //     fontSize: "12px",
      //     lineHeight: 1.5,
      //   },
      //   "& h1, & h2, & h3, & h4, & h5, & h6": {
      //     fontFamily: "var(--primaryFont)",
      //     marginTop: "0.6em",
      //     marginBottom: "0.3em",
      //   },
      //   "& h6": {
      //     fontSize: "12px",
      //     textTransform: "uppercase",
      //     letterSpacing: "0.04em",
      //     fontWeight: 700,
      //   },
      //   "& a": {
      //     color: theme.palette.primary?.main || "#1976d2",
      //     textDecoration: "none",
      //     "&:hover": { textDecoration: "underline" },
      //   },
      //   "& .wysiwyg-mention": {
      //     color: theme.palette.primary?.main || "#1976d2",
      //     fontWeight: 600,
      //     cursor: "pointer",
      //     "&:hover": { textDecoration: "underline" },
      //   },
      //   "& .tiptap p.is-editor-empty:first-child::before": {
      //     content: "attr(data-placeholder)",
      //     float: "left",
      //     color: "#adb5bd",
      //     pointerEvents: "none",
      //     height: 0,
      //     fontStyle: "italic",
      //     fontSize: "12px",
      //   },

      //   ...editorMainSx,
      // },

      ...sx,
    };
  }, [
    borderRadius,
    suppressOuterBorder,
    editorRootSx,
    editorMainSx,
    backgroundColor,
    minHeight,
    maxHeight,
    sx,
    theme,
    readOnly,
    isEditorEmpty,
  ]);

  return (
    <Box className="html-text-editor-root" id={id} sx={rootStyles}>
      {!readOnly && (alwaysShowToolbar || !isEditorEmpty) && (
        <EditorToolbar
          editor={editor}
          enableTaskList={enableTaskList}
          enableImages={enableImages}
        />
      )}
      <EditorContent editor={editor} className="tiptap-editor-content" />
      {children}
    </Box>
  );
};

// ─── Utility: Extract all @mentions from editor HTML ──────────────────────────

export const getAllTipTapMentions = (htmlString) => {
  if (!htmlString) return [];
  const mentions = [];
  const regex = /data-value="([^"]+)"/g;
  let match;
  while ((match = regex.exec(htmlString)) !== null) {
    if (match[1] && match[1].includes("@")) {
      mentions.push(match[1]);
    }
  }
  return [...new Set(mentions)];
};

// Backward-compat alias — GenericEmailEditor calls getAllDraftJsMentions
export const getAllDraftJsMentions = (editorStateOrHtml) => {
  // If someone passes an HTML string, parse mentions from it
  if (isString(editorStateOrHtml)) {
    return getAllTipTapMentions(editorStateOrHtml);
  }
  // If it's the TipTap editor instance
  if (editorStateOrHtml?.getHTML) {
    return getAllTipTapMentions(editorStateOrHtml.getHTML());
  }
  return [];
};

export default HtmlEditor;
